// App.tsx
import React, { Suspense, useEffect } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ErrorBoundary } from "./modules/errors/ErrorBoundary";
import queryClient from "./config/queryClient";
import AppRouter from "./router/Router";
import { I18nextProvider } from "react-i18next";

import { ThemeProvider } from "context/ThemeContext";
import { GlobalStateProvider } from "context/GlobalStateContext";
import ToastProvider, { useToast } from "context/ToastContext";
import InitializeToastHelper from "helpers/InitializeToastHelper";

import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { fetchCompanyById } from "store/thunks/companyThunks";
import { fetchVehicleDataThunk } from "store/thunks/vehicleSpecThunks";
import { listenForegroundMessages, registerWebFcmToken } from "@utils/webFcm";
import { ensureWsConnected, subscribeWs } from "@utils/socket";
import i18n from "locales/i18n";

// ✅ move all logic that needs showToast INSIDE provider
const AppShell: React.FC = () => {
	const dispatch = useDispatch<AppDispatch>();
	const auth = useSelector((s: RootState) => s.auth);
	const token = auth.data.access_token;

	const isAuthenticated = useSelector(
		(state: RootState) => state.auth.isAuthenticated,
	);
	const companyId = useSelector(
		(state: RootState) => state.auth.data?.user?.company_id,
	);
	const companyFetched = useSelector((state: RootState) => state.company.data);
	const vehicles = useSelector((state: RootState) => state.vehicles.data);
	const packagingData = useSelector((state: RootState) => state.packaging.data);

	// ✅ now you can use it safely
	const { showToast } = useToast();

	const toastInfo = (message?: string) => {
		if (!message) return;

		const lang = (i18n.language || "ru").split("-")[0];

		const prefixMap: Record<string, string> = {
			ru: "У вас новое сообщение: ",
			en: "You have a new message: ",
			tr: "Yeni bir mesajınız var: ",
		};

		showToast({
			message: `${prefixMap[lang] ?? prefixMap.ru}${message}`,
			status: "info",
			position: "top-right",
			autoClose: true,
			duration: 5000,
		});
	};

	useEffect(() => {
		if (!token) return;

		registerWebFcmToken(token).catch((err) => {
			console.warn("[FCM] Token registration failed", err);
		});

		const emitEvents = (data: any) => {
			if (data?.type === "message" && data?.conversation_id) {
				window.dispatchEvent(
					new CustomEvent("chat_message_received", {
						detail: { conversation_id: Number(data.conversation_id) },
					}),
				);
			}

			if (data?.type === "offer_response") {
				window.dispatchEvent(
					new CustomEvent("offer_response_received", {
						detail: {
							offerId: Number(data.offer_id),
							offerResponseId: Number(data.offer_response_id),
							state: data.state,
							createdAt: data.created_at,
						},
					}),
				);
			}
		};

		const unsubFcm = listenForegroundMessages((payload) => {
			const data: any = payload?.data || {};
			if (data?.is_silent === "1") return;

			toastInfo(
				payload?.notification?.body || data?.content || "New notification",
			);
			emitEvents(data);
		});

		ensureWsConnected(token);

		const unsubWs = subscribeWs((msg) => {
			const content = msg?.content ?? msg?.Content ?? "";
			const extras = msg?.extras ?? msg?.Extras ?? msg ?? {};

			const isSilent = String(extras?.is_silent ?? "0") === "1";
			if (!isSilent) toastInfo(content || extras?.content);

			emitEvents(extras);
		});

		return () => {
			unsubFcm?.();
			unsubWs?.();
		};
	}, [token]);

	// ✅ quick test (remove later)
	// useEffect(() => {
	// 	toastInfo("Test message");
	// }, []);

	useEffect(() => {
		if (packagingData.length === 0) {
			(async () => {
				const { fetchPackagingTypesThunk } = await import(
					"store/thunks/packagingThunks"
				);
				dispatch(fetchPackagingTypesThunk())
					.unwrap()
					.catch((err) =>
						console.error("❌ Failed to fetch packaging types:", err),
					);
			})();
		}
	}, [packagingData, dispatch]);

	useEffect(() => {
		const fetchCompanyDetails = async () => {
			if (isAuthenticated && companyId && !companyFetched) {
				try {
					await dispatch(fetchCompanyById(companyId)).unwrap();
				} catch (error) {
					console.error("Failed to fetch company details:", error);
				}
			}
		};

		fetchCompanyDetails();
	}, [isAuthenticated, companyId, companyFetched, dispatch]);

	useEffect(() => {
		if (
			vehicles.brands.length === 0 ||
			vehicles.types.length === 0 ||
			vehicles.models.length === 0
		) {
			dispatch(fetchVehicleDataThunk())
				.unwrap()
				.catch((error) =>
					console.error("❌ Failed to fetch vehicle data:", error),
				);
		}
	}, [vehicles, dispatch]);

	return (
		<QueryClientProvider client={queryClient}>
			<I18nextProvider i18n={i18n}>
				<Suspense fallback={<div>Loading...</div>}>
					<AppRouter />
				</Suspense>
			</I18nextProvider>

			{process.env.NODE_ENV === "development" && (
				<ReactQueryDevtools initialIsOpen={false} position="bottom" />
			)}
		</QueryClientProvider>
	);
};

const App: React.FC = () => {
	return (
		<ErrorBoundary>
			<GlobalStateProvider>
				<ThemeProvider>
					<ToastProvider>
						<InitializeToastHelper />
						<AppShell />
					</ToastProvider>
				</ThemeProvider>
			</GlobalStateProvider>
		</ErrorBoundary>
	);
};

export default App;
